import { lazy, Suspense, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { queryClient, persistOptions } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import MobileShell from "./components/MobileShell";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminGate from "./components/AdminGate";
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import OnboardingSlides from "./components/OnboardingSlides";

// ── Authenticated app pages — loaded on first navigation to each route ─────
const Home = lazy(() => import("./pages/Home"));
const Feed = lazy(() => import("./pages/Feed"));
const MyTribe = lazy(() => import("./pages/MyTribe"));
const CollectionsPage = lazy(() => import("./pages/Collections"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const StashPage = lazy(() => import("./pages/Stash"));
const UpgradePage = lazy(() => import("./pages/Upgrade"));
const MemoryDetail = lazy(() => import("./pages/MemoryDetail"));
const CaptureEntry = lazy(() => import("./pages/CaptureEntry"));
const AccountLocked = lazy(() => import("./pages/AccountLocked"));
const WelcomeGift = lazy(() => import("./pages/WelcomeGift"));

// ── Public / pre-auth pages — grouped into a separate "public" chunk ───────
const AuthPage = lazy(() => import("./pages/Auth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const InvitePage = lazy(() => import("./pages/Invite"));
const SharedMemory = lazy(() => import("./pages/SharedMemory"));
const GiftPage = lazy(() => import("./pages/GiftPage"));
const GiftSuccessPage = lazy(() => import("./pages/GiftSuccessPage"));
const GiftAlreadyRedeemedPage = lazy(() => import("./pages/GiftAlreadyRedeemedPage"));
const RedeemExpiredPage = lazy(() => import("./pages/RedeemExpiredPage"));
const RedeemPage = lazy(() => import("./pages/RedeemPage"));
const GiftCannotStackPage = lazy(() => import("./pages/GiftCannotStackPage"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));

// ── Admin pages — grouped into a separate "admin" chunk ────────────────────
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const TranslationManager = lazy(() => import("./pages/admin/TranslationManager"));
const PromptEditor = lazy(() => import("./pages/admin/PromptEditor"));
const ContentLibraryEditor = lazy(() => import("./pages/admin/ContentLibraryEditor"));
const CollectionTemplateEditor = lazy(() => import("./pages/admin/CollectionTemplateEditor"));
const CategoryManager = lazy(() => import("./pages/admin/CategoryManager"));
const AcornRulesEditor = lazy(() => import("./pages/admin/AcornRulesEditor"));
const StashEditor = lazy(() => import("./pages/admin/StashEditor"));
const PeopleDashboard = lazy(() => import("./pages/admin/PeopleDashboard"));
const MemoriesDashboard = lazy(() => import("./pages/admin/MemoriesDashboard"));
const BusinessDashboard = lazy(() => import("./pages/admin/BusinessDashboard"));
const AiDashboard = lazy(() => import("./pages/admin/AiDashboard"));
const AiDebugDashboard = lazy(() => import("./pages/admin/AiDebugDashboard"));
const EmailTemplateEditor = lazy(() => import("./pages/admin/EmailTemplateEditor"));
const CardFrequency = lazy(() => import("./pages/admin/CardFrequency"));
const FeedbackDashboard = lazy(() => import("./pages/admin/FeedbackDashboard"));
const GrantsManager = lazy(() => import("./pages/admin/GrantsManager"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const MemoryMetadataPage = lazy(() => import("./pages/admin/MemoryMetadata"));
const PartnersAdmin = lazy(() => import("./pages/admin/PartnersAdmin"));
const PartnerReports = lazy(() => import("./pages/admin/PartnerReports"));
import { useAuth, resolveActiveSpaceRole, seedActiveSpaceRoleCache } from "./hooks/useAuth";
import { seedIsChildRoleCache } from "./hooks/useIsChildRole";
import { usePWABackGuard } from "./hooks/usePWABackGuard";
import { supabase } from "./lib/supabase";
import { AcornProvider } from "./contexts/AcornContext";
import { DeferredIntentProvider } from "./contexts/DeferredIntentContext";
import { useTranslationOverrides } from "./hooks/useTranslationOverrides";
import { fireEvent } from "./lib/events";
import { captureRefParam, claimAttributionIfPending } from "@/lib/partnerAttribution";
import ConsentBanner from "./components/consent/ConsentBanner";
import { useConsent } from "@/lib/consent/useConsent";
import { maybeLoadReplay } from "@/lib/consent/sentryReplayLoader";

// Wires consent state to Sentry Replay. Rendered inside BrowserRouter so it
// shares the same QueryClientProvider context as the rest of the app.
function ConsentEffects() {
  const { consent, inConsentRegion } = useConsent();
  useEffect(() => {
    maybeLoadReplay(consent, inConsentRegion);
  }, [consent, inConsentRegion]);
  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useTranslationOverrides();
  usePWABackGuard();
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const [showSlides, setShowSlides] = useState<boolean | null>(null);
  const [slidesLocale, setSlidesLocale] = useState<string>("en");
  const [isChildRole, setIsChildRole] = useState<boolean | null>(null);
  const [childNickname, setChildNickname] = useState("");

  // ── Gift token capture ────────────────────────────────────────────────────
  // /auth?gift_token=xxx is the unauth redirect target from gift-redeem and
  // RedeemPage. Stash the token so the post-auth effect below can route
  // back to /redeem once the user is signed in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get("gift_token");
    if (token) {
      sessionStorage.setItem("mori_gift_token", token);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // ── Partner attribution capture ───────────────────────────────────────────
  // On landing with ?ref=<code>: stash the attribution token in localStorage,
  // strip the param, and start the 3-second engagement gate before logging.
  useEffect(() => {
    captureRefParam();
  }, []);

  // ── Post-auth: hand the token to /redeem, which calls gift-redeem and
  // routes the user to /welcome-gift, /gift/already-redeemed,
  // /gift/cannot-stack, or /redeem-expired based on the JSON result.
  useEffect(() => {
    if (!user) return;
    const token = sessionStorage.getItem("mori_gift_token");
    if (!token) return;
    sessionStorage.removeItem("mori_gift_token");
    navigate(`/redeem?token=${encodeURIComponent(token)}`, { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) {
      setNeedsOnboarding(null);
      setShowSlides(null);
      setSlidesLocale("en");
      setIsChildRole(null);
      setChildNickname("");
      return;
    }

    // Google OAuth intent check: if the user stored "signin" intent but they're brand-new
    // (created within the last 30 s), they used Sign In with Google without an account.
    // Sign them out and send them back to the auth page with an error flag.
    try {
      const oauthIntent = localStorage.getItem("mori.oauth_intent");
      if (oauthIntent) {
        localStorage.removeItem("mori.oauth_intent");
        const isNewUser = Date.now() - new Date(user.created_at).getTime() < 30_000;
        if (oauthIntent === "signin" && isNewUser) {
          localStorage.setItem("mori.oauth_error", "no_account");
          void supabase.auth.signOut();
          return;
        }
      }
    } catch { /* storage disabled */ }

    // Fetch role + children + profile in parallel to determine routing.
    // `first_open_completed` is the server-side gate for the welcome slides —
    // reading it from localStorage was the old behaviour and caused repeat
    // showings (and repeat acorn awards) on every cache clear.
    void Promise.all([
      supabase.from("children").select("id").limit(1),
      supabase
        .from("profiles")
        .select("nickname, language, first_open_completed")
        .eq("id", user.id)
        .single(),
      // Seed the useAuth role cache so every useAuth consumer reads the
      // active-space role synchronously on first render — prevents a
      // flash of adult UI for child/contributor accounts while each
      // consumer's per-instance fetch would otherwise still be in flight.
      resolveActiveSpaceRole(user.id),
    ]).then(([{ data: childData }, { data: profileData }, activeRole]) => {
      seedActiveSpaceRoleCache(user.id, activeRole);
      const isChild = activeRole === "child";
      setIsChildRole(isChild);
      // Seed the useIsChildRole module cache so downstream surfaces
      // (MemoryMetadataScreen, captureProcessor) see the role synchronously
      // — avoids a cold-cache race if the user taps Save before the hook's
      // own async resolution lands.
      seedIsChildRoleCache(user.id, isChild);
      const profile =
        (profileData as {
          nickname: string | null;
          language: string | null;
          first_open_completed: boolean | null;
        } | null) ?? null;
      setChildNickname(profile?.nickname ?? "");
      setSlidesLocale(profile?.language ?? "en");
      // Child users skip the welcome slides and profile-completion flow.
      // Missing profile row → treat as first open (new user).
      const skipAdultOnboarding = isChild;
      setShowSlides(skipAdultOnboarding ? false : !(profile?.first_open_completed ?? false));
      setNeedsOnboarding(skipAdultOnboarding ? false : !childData || childData.length === 0);
      // Fire-and-forget: claim any pending partner attribution now that the
      // session is confirmed live. Does not block routing on the result.
      void claimAttributionIfPending();
    }).catch((err) => {
      // Without this, a transient network/RLS failure leaves the three
      // routing flags as null forever — the splash in App.tsx hangs and
      // the user is stuck. Degrade to "land them on Home" instead: assume
      // not-a-child, slides already seen, and onboarding already done.
      // Worst case the user briefly sees Home with empty data while the
      // session re-resolves; far better than an indefinite splash or a
      // false re-onboarding trigger from a one-shot query failure.
      Sentry.captureException(err, { tags: { flow: "app-routing-resolve" } });
      setIsChildRole(false);
      setShowSlides(false);
      setNeedsOnboarding(false);
    });
  }, [user]);

  // ── streak.broken detection — fires when user skipped one or more days ──────
  // Runs on every login. If streak > 0 and last_debrief_date < yesterday,
  // the streak was broken. We reset it to 0 in DB so the next open won't re-fire.
  useEffect(() => {
    if (!user) return;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    supabase
      .from("profiles")
      .select("current_streak, last_debrief_date")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        const p = data as { current_streak: number; last_debrief_date: string | null } | null;
        if (!p || p.current_streak <= 0 || !p.last_debrief_date) return;
        if (p.last_debrief_date >= yesterdayStr) return; // intact
        const previousStreak = p.current_streak;
        void supabase
          .from("profiles")
          .update({ current_streak: 0 })
          .eq("id", user.id);
        fireEvent("streak.broken", { userId: user.id, previousStreak });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // `app.first.open` fires from inside OnboardingSlides.finish() now, so it
  // lands in lockstep with `intro.completed` and the DB flag write. The old
  // parallel useEffect here was racy with the slide completion and is gone.

  // Splash screen while auth or role/onboarding checks are in flight
  if (
    loading ||
    (user && (needsOnboarding === null || isChildRole === null || showSlides === null))
  ) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.06] animate-float" />
          <div className="absolute top-1/3 -left-32 h-96 w-96 rounded-full bg-accent/[0.07] animate-float-slow" />
          <div className="absolute -bottom-16 right-10 h-56 w-56 rounded-full bg-primary/[0.04] animate-float-slower" />
          <div className="absolute bottom-1/4 left-1/4 h-40 w-40 rounded-[40%_60%_55%_45%/50%_40%_60%_50%] bg-accent/[0.05] animate-float-slow" />
        </div>
        <div className="relative z-10 text-center">
          <img src="/logo-white.svg" alt="Mori" className="mx-auto w-28 h-28" />
        </div>
      </div>
    );
  }

  // Step 1 — welcome slide deck (only first time ever, gated server-side
  // by profiles.first_open_completed so cache-clears can't retrigger it).
  if (user && showSlides) {
    return (
      <OnboardingSlides
        userId={user.id}
        locale={slidesLocale}
        onComplete={() => setShowSlides(false)}
      />
    );
  }

  // Step 2 — profile completion (nickname + first child + language)
  if (user && needsOnboarding) {
    return (
      <OnboardingFlow
        userId={user.id}
        onComplete={() => setNeedsOnboarding(false)}
      />
    );
  }

  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    }>
    <Routes>
      {/* Public pages — no auth required */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Auth — redirect to dashboard if already signed in */}
      <Route
        path="/auth"
        element={user ? <Navigate to="/" replace /> : <AuthPage />}
      />

      {/* Past-due hard gate — outside MobileShell so the gate itself
          doesn't redirect-loop against the guard. */}
      <Route
        path="/account-locked"
        element={!user ? <Navigate to="/auth" replace /> : <AccountLocked />}
      />

      {/* Authenticated app shell with bottom nav */}
      <Route element={!user ? <Navigate to="/auth" replace /> : <MobileShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/tribe" element={<MyTribe />} />
        <Route path="/collections" element={<CollectionsPage />} />
        {/* Legacy redirect — keep for one release cycle after Collections ships. */}
        <Route path="/themes" element={<Navigate to="/collections" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/stash" element={<StashPage />} />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/capture" element={<CaptureEntry />} />
        <Route path="/memories/:id" element={<MemoryDetail />} />
        {/* Gift welcome — only reachable via gift redemption redirect */}
        <Route path="/welcome-gift" element={<WelcomeGift />} />
      </Route>

      {/* Admin routes — email allowlist gate */}
      <Route element={<AdminGate />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="people" replace />} />
          <Route path="people" element={<PeopleDashboard />} />
          <Route path="memories" element={<MemoriesDashboard />} />
          <Route path="business" element={<BusinessDashboard />} />
          <Route path="grants" element={<GrantsManager />} />
          <Route path="ai" element={<AiDashboard />} />
          <Route path="ai-debug" element={<AiDebugDashboard />} />
          <Route path="translations" element={<TranslationManager />} />
          <Route path="prompts" element={<PromptEditor />} />
          <Route path="content" element={<ContentLibraryEditor />} />
          <Route path="card-frequency" element={<CardFrequency />} />
          <Route path="collections" element={<CollectionTemplateEditor />} />
          {/* Legacy redirect — keep for one release cycle. */}
          <Route path="themes" element={<Navigate to="/admin/collections" replace />} />
          <Route path="categories" element={<CategoryManager />} />
          <Route path="acorns" element={<AcornRulesEditor />} />
          <Route path="stash" element={<StashEditor />} />
          <Route path="partners" element={<PartnersAdmin />} />
          <Route path="partner-reports" element={<PartnerReports />} />
          <Route path="emails" element={<EmailTemplateEditor />} />
          <Route path="feedback" element={<FeedbackDashboard />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="memory-metadata" element={<MemoryMetadataPage />} />
        </Route>
      </Route>

      {/* Public routes — no auth required */}
      <Route path="/email-preferences" element={<EmailPreferences />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/s/:token" element={<SharedMemory />} />
      <Route path="/gift" element={<GiftPage />} />
      <Route path="/gift/success" element={<GiftSuccessPage />} />
      <Route path="/gift/already-redeemed" element={<GiftAlreadyRedeemedPage />} />
      <Route path="/gift/cannot-stack" element={<GiftCannotStackPage />} />
      <Route path="/redeem" element={<RedeemPage />} />
      <Route path="/redeem-expired" element={<RedeemExpiredPage />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

const App = () => (
  <ErrorBoundary>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <TooltipProvider>
        {/* Radix shadcn toaster — for OnboardingFlow (object-syntax API).
            Pages outside MobileShell (Auth, Invite, etc.) mount their own Sonner. */}
        <Toaster />
        <AcornProvider>
          <DeferredIntentProvider>
            <BrowserRouter>
              <ConsentBanner />
              <ConsentEffects />
              <AppRoutes />
            </BrowserRouter>
          </DeferredIntentProvider>
        </AcornProvider>
      </TooltipProvider>
    </PersistQueryClientProvider>
  </ErrorBoundary>
);

export default App;
